import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, QrCode, CheckCircle2, ShieldAlert, Building2, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// Guest-raise page reachable at /raise/:outletId. NO auth required — that's the whole point.
// Anyone with the printed QR can raise a ticket. Server enforces a monthly cap across all outlets.
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = [
  { v: "maintenance", label: "Maintenance" },
  { v: "product_issue", label: "Product issue" },
  { v: "structural_damage", label: "Structural damage" },
  { v: "new_installation", label: "New installation" },
  { v: "miscellaneous", label: "Miscellaneous" },
];
const PRIORITIES = [
  { v: "urgent", label: "Urgent" },
  { v: "general", label: "General" },
  { v: "routine", label: "Routine" },
];

export default function PublicRaise() {
  const { outletId: paramOutletId } = useParams();
  const [search] = useSearchParams();
  const outletId = paramOutletId || search.get("outlet") || "";

  const [outlet, setOutlet] = useState(null);
  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [name, setName] = useState("");
  const [type, setType] = useState("maintenance");
  const [priority, setPriority] = useState("general");
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // { id, title, quota }

  useEffect(() => {
    if (!outletId) { setError("This QR code doesn't include an outlet."); setLoading(false); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/public/outlets/${outletId}`);
        setOutlet(data.outlet);
        setQuota(data.quota);
      } catch (e) {
        setError(e?.response?.data?.detail || "Outlet not found — please ask staff to check this QR code.");
      } finally { setLoading(false); }
    })();
  }, [outletId]);

  const submit = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2) return toast.error("Please enter your name");
    if (title.trim().length < 3) return toast.error("Please describe the issue in the title");
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API_BASE}/public/tickets`, {
        outlet_id: outletId,
        initiator_name: name.trim(),
        type, priority,
        title: title.trim(),
        description: desc.trim(),
      });
      setSubmitted(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not raise ticket");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-lg mx-auto p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-9 h-9 rounded-lg bg-indigo-600 text-white grid place-items-center">
            <QrCode className="w-4 h-4" />
          </div>
          <div>
            <div className="font-display font-bold text-slate-900">Job Ticker</div>
            <div className="text-[11px] text-slate-500 -mt-0.5">Scan-to-raise</div>
          </div>
        </div>

        {loading ? (
          <div className="rounded-2xl bg-white border border-slate-200 p-8 grid place-items-center text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : error ? (
          <div className="rounded-2xl bg-white border border-rose-200 p-6 text-center">
            <ShieldAlert className="w-8 h-8 text-rose-600 mx-auto" />
            <div className="mt-3 font-semibold text-slate-900">Can't open this QR</div>
            <div className="text-sm text-slate-500 mt-1">{error}</div>
          </div>
        ) : submitted ? (
          <div data-testid="guest-success" className="rounded-2xl bg-white border border-emerald-200 p-6 text-center">
            <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
            <div className="mt-3 font-display font-bold text-xl text-slate-900">Thanks {name.split(" ")[0]}!</div>
            <div className="text-sm text-slate-600 mt-1">
              Your ticket for <span className="font-semibold text-slate-800">{outlet.name}</span> has been raised. Staff will pick it up shortly.
            </div>
            <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-slate-100 border border-slate-200 text-slate-700 px-3 py-1 text-xs">
              <Info className="w-3 h-3" /> Ticket ref · <span className="font-mono font-semibold">{submitted.id.slice(0, 8)}</span>
            </div>
            <div className="text-[11px] text-slate-400 mt-4">
              Guest tickets remaining this month: <span className="font-semibold text-slate-600">{submitted.quota.remaining}</span> / {submitted.quota.limit}
            </div>
            <div className="mt-6">
              <Button
                data-testid="guest-raise-another"
                onClick={() => { setSubmitted(null); setTitle(""); setDesc(""); }}
                variant="outline"
              >
                Raise another
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="rounded-2xl bg-white border border-slate-200 p-5 mb-4">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Raising for</div>
              <div className="mt-1 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-slate-500" />
                <span className="font-display font-bold text-slate-900">{outlet.name}</span>
              </div>
              <div className="text-xs text-slate-500 mt-0.5">Outlet code · <span className="font-semibold text-slate-700">{outlet.code}</span></div>
              {quota && quota.remaining <= 3 && (
                <div className={`mt-3 rounded-lg px-3 py-2 text-xs ${quota.remaining === 0 ? "bg-rose-50 text-rose-800 border border-rose-200" : "bg-amber-50 text-amber-900 border border-amber-200"}`}>
                  {quota.remaining === 0
                    ? `Monthly guest limit of ${quota.limit} tickets is exhausted. Please ask staff to raise this for you.`
                    : `Only ${quota.remaining} guest ticket${quota.remaining === 1 ? "" : "s"} left across all outlets this month.`}
                </div>
              )}
            </div>

            <form onSubmit={submit} className="rounded-2xl bg-white border border-slate-200 p-5 space-y-4">
              <div>
                <Label>Your name</Label>
                <Input data-testid="guest-name" required value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 h-11" placeholder="Full name" maxLength={80} />
                <div className="text-[11px] text-slate-400 mt-1">This gets logged as the ticket initiator.</div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Type</Label>
                  <Select value={type} onValueChange={setType}>
                    <SelectTrigger data-testid="guest-type" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TYPES.map(t => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select value={priority} onValueChange={setPriority}>
                    <SelectTrigger data-testid="guest-priority" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map(p => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>What's the issue?</Label>
                <Input data-testid="guest-title" required value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1.5 h-11" placeholder="Short summary" maxLength={140} />
              </div>

              <div>
                <Label>Details <span className="text-slate-400 font-normal">(optional)</span></Label>
                <Textarea data-testid="guest-desc" value={desc} onChange={(e) => setDesc(e.target.value)} className="mt-1.5 min-h-24" placeholder="Anything else the team should know" />
              </div>

              <Button
                type="submit"
                disabled={submitting || (quota && quota.remaining === 0)}
                data-testid="guest-submit"
                className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Raise ticket"}
              </Button>

              <div className="text-center text-[11px] text-slate-400">
                Staff? <Link to="/login" className="text-indigo-600 hover:underline">Sign in</Link> to see all tickets.
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
